import L from 'leaflet';
import { useEffect, useRef } from 'react';
import { useMap } from 'react-leaflet';
import { geoSidoCodeToOpinet, geoSigunguRegionKey } from '@/lib/region-code-mapper';
import { getCode, getName, getParentCode, loadGeoJSON, SIDO_URL, SIGUNGU_URL } from './korea-region-layer';
import { NO_DATA_COLOR, type ChoroplethScale } from '@/constants/sgis-metrics';
import type { HeatLevel } from '@/lib/price-analysis';

interface PriceHeatLayerProps {
  active: boolean;
  level: HeatLevel;
  /** 지역 키("서울" 또는 "서울|강남구") → 평균가 */
  values: Map<string, number>;
  scale: ChoroplethScale | null;
}

/** 시도/시군구 폴리곤을 평균 유가로 채색하는 비대화형 레이어 */
export function PriceHeatLayer({ active, level, values, scale }: PriceHeatLayerProps) {
  const map = useMap();
  const layerRef = useRef<L.GeoJSON | null>(null);

  const keyOf = (props: Record<string, unknown>): string =>
    level === 'sido'
      ? geoSidoCodeToOpinet(getCode(props))
      : geoSigunguRegionKey(getCode(props), getParentCode(props), getName(props));

  useEffect(() => {
    if (!active) {
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
      return;
    }
    let cancelled = false;
    const load = async () => {
      try {
        const data = await loadGeoJSON(level === 'sido' ? SIDO_URL : SIGUNGU_URL);
        if (cancelled) return;
        if (layerRef.current) map.removeLayer(layerRef.current);
        const layer = L.geoJSON(data, {
          interactive: false,
          style: feature => {
            const props = (feature?.properties ?? {}) as Record<string, unknown>;
            return { color: '#6b7280', weight: 0.8, fillColor: scale?.colorFor(values.get(keyOf(props))) ?? NO_DATA_COLOR, fillOpacity: 0.55 };
          },
          onEachFeature: (feature, featureLayer) => {
            const props = feature.properties as Record<string, unknown>;
            const v = values.get(keyOf(props));
            featureLayer.bindTooltip(`${getName(props)} ${v !== undefined ? `${v.toLocaleString()}원` : '자료 없음'}`, {
              sticky: true,
              direction: 'top',
            });
          },
        });
        layer.addTo(map);
        layerRef.current = layer;
      } catch (err) {
        console.error('가격 히트맵 GeoJSON 로드 실패:', err);
      }
    };
    load();
    return () => {
      cancelled = true;
      if (layerRef.current) {
        map.removeLayer(layerRef.current);
        layerRef.current = null;
      }
    };
  }, [active, level, values, scale, map]);

  return null;
}
